import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Menu, X, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useNotificationContext } from '../../screens/Notifications/NotificationContext.jsx';
// Import new icons from NewIcon folder
import { ReactComponent as IconAcasa } from '../../NewIcon/IconAcasa.svg';
import { ReactComponent as IconTransporturiActiveStatistici } from '../../NewIcon/IconTransporturiActiveStatistici.svg';
import { ReactComponent as IconSofer } from '../../NewIcon/IconSofer.svg';
import { ReactComponent as IconCamion } from '../../NewIcon/IconCamion.svg';
import { ReactComponent as IconDuba } from '../../NewIcon/IconDuba.svg';
import { ReactComponent as IconMasinaMica } from '../../NewIcon/IconMasinaMica.svg';
import { ReactComponent as IconRemorca } from '../../NewIcon/IconRemorca.svg';
import { ReactComponent as IconInterventiiService } from '../../NewIcon/IconInterventiiService.svg';
import { ReactComponent as IconCMR } from '../../NewIcon/IconCMR.svg';
import { ReactComponent as IconUIT } from '../../NewIcon/IconUIT.svg';
import { ReactComponent as IconNotificari } from '../../NewIcon/IconNotificari.svg';
import { ReactComponent as IconProfil } from '../../NewIcon/IconProfil.svg';
import { ReactComponent as IconProfitabilitate } from '../../NewIcon/IconProfitabilitate.svg';
import { ReactComponent as IconSemnulExclamarii } from '../../NewIcon/IconSemnulExclamarii.svg';
import { IconFacturi } from '../General/PlatformIcons.jsx';
import AtruxLogo from '../../assets/logo_final.png';
import { FEATURE_CODES, hasFeature } from '../../utils/features.js';
import { isChrome as detectChrome, isSafari as detectSafari } from '../../utils/browserDetection.js';

const ModernSidebar = ({ userData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  const navigate = useNavigate();
  const location = useLocation();
  const { notifications } = useNotificationContext();
  const { t } = useTranslation('navigation');

  const excludedNotificationTypes = useMemo(
    () => new Set(['system_alert', 'system_message', 'system_notification']),
    []
  );
  const unreadNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const type = notification.notification_type || notification.type;
      if (excludedNotificationTypes.has(type)) return false;
      if (typeof notification.is_read === 'boolean') return !notification.is_read;
      if (typeof notification.read === 'boolean') return !notification.read;
      return true;
    });
  }, [notifications, excludedNotificationTypes]);
  const unreadCount = unreadNotifications.length;

  const TransporturiSidebarIcon = ({ size = 20, className = '' }) => (
    <IconTransporturiActiveStatistici width={size} height={size} className={className} />
  );
  const SoferiSidebarIcon = ({ size = 20, className = '' }) => (
    <IconSofer width={size} height={size} className={className} />
  );
  const CamioaneSidebarIcon = ({ size = 20, className = '' }) => (
    <IconCamion width={size} height={size} className={className} />
  );
  const AutoturismeSidebarIcon = ({ size = 20, className = '' }) => (
    <IconMasinaMica width={size} height={size} className={className} />
  );
  const DubeSidebarIcon = ({ size = 20, className = '' }) => (
    <IconDuba width={size} height={size} className={className} />
  );
  const RemorciSidebarIcon = ({ size = 20, className = '' }) => (
    <IconRemorca width={size} height={size} className={className} />
  );
  const CmrSidebarIcon = ({ size = 20, className = '' }) => (
    <IconCMR width={size} height={size} className={className} />
  );
  const UitSidebarIcon = ({ size = 20, className = '' }) => (
    <IconUIT width={size} height={size} className={className} />
  );
  const ServiceSidebarIcon = ({ size = 20, className = '' }) => (
    <IconInterventiiService width={size} height={size} className={className} />
  );
  const DashboardSidebarIcon = ({ size = 20, className = '' }) => (
    <IconAcasa width={size} height={size} className={className} />
  );
  const ProfilSidebarIcon = ({ size = 20, className }) => (
    <IconProfil width={size} height={size} className={className} />
  );
  const ProfitabilitySidebarIcon = ({ size = 20, className = '' }) => (
    <IconProfitabilitate width={size} height={size} className={className} />
  );
  const ExpiredDocumentsSidebarIcon = ({ size = 20, className = '' }) => (
    <IconSemnulExclamarii width={size} height={size} className={className} />
  );
  const BillingSidebarIcon = ({ size = 20, className = '' }) => (
    <IconFacturi width={size} height={size} className={className} />
  );

  // Detect browser
  const isSafari = useMemo(detectSafari, []);
  const isChrome = useMemo(detectChrome, []);

  // Get menu items based on user role
  const getMenuItems = () => {
    const userRole = userData?.role?.toLowerCase() || '';

    const allItems = [
      { id: 'dashboard', label: t('dashboard'), icon: DashboardSidebarIcon, path: '/main', roles: ['all'] },
      { id: 'transports', label: t('transports'), icon: TransporturiSidebarIcon, path: '/transports', roles: ['dispatcher', 'admin', 'economist'], featureCode: FEATURE_CODES.TRANSPORT_MANAGEMENT },
      { id: 'drivers', label: t('drivers'), icon: SoferiSidebarIcon, path: '/drivers', roles: ['dispatcher', 'admin', 'economist'], featureCode: FEATURE_CODES.WORKFORCE_MANAGEMENT },
      { id: 'trucks', label: t('trucks'), icon: CamioaneSidebarIcon, path: userRole === 'fleet_manager' ? '/fleet/trucks' : '/trucks', roles: ['dispatcher', 'admin', 'fleet_manager', 'economist'], featureCode: FEATURE_CODES.FLEET_MANAGEMENT },
      { id: 'trailers', label: t('trailers'), icon: RemorciSidebarIcon, path: userRole === 'fleet_manager' ? '/fleet/trailers' : '/trailers', roles: ['dispatcher', 'admin', 'fleet_manager', 'economist'], featureCode: FEATURE_CODES.FLEET_MANAGEMENT },
      { id: 'cars', label: t('cars'), icon: AutoturismeSidebarIcon, path: '/fleet/cars', roles: ['all'], featureCode: FEATURE_CODES.FLEET_MANAGEMENT },
      { id: 'vans', label: t('vans'), icon: DubeSidebarIcon, path: '/fleet/vans', roles: ['all'], featureCode: FEATURE_CODES.FLEET_MANAGEMENT },
      { id: 'maintenance', label: t('maintenance'), icon: ServiceSidebarIcon, path: '/fleet/maintenance', roles: ['dispatcher', 'admin', 'fleet_manager'], featureCode: FEATURE_CODES.FLEET_MANAGEMENT },
      { id: 'service', label: t('service'), icon: ServiceSidebarIcon, path: '/fleet/interventions', roles: ['dispatcher', 'admin', 'fleet_manager'], featureCode: FEATURE_CODES.FLEET_MANAGEMENT },
      { id: 'expired-documents', label: t('expiredDocuments'), icon: ExpiredDocumentsSidebarIcon, path: '/expired-documents', roles: ['admin', 'fleet_manager'], featureCodesAny: [FEATURE_CODES.FLEET_MANAGEMENT, FEATURE_CODES.WORKFORCE_MANAGEMENT] },
      { id: 'cmr', label: t('cmr'), icon: CmrSidebarIcon, path: '/cmr-management', roles: ['dispatcher', 'admin'], featureCodes: [FEATURE_CODES.TRANSPORT_MANAGEMENT, FEATURE_CODES.ECMR] },
      { id: 'uit', label: t('uit'), icon: UitSidebarIcon, path: '/uit', roles: ['dispatcher', 'admin'], featureCode: FEATURE_CODES.ETRANSPORT },
      { id: 'freight-billing', label: t('freightBilling'), icon: BillingSidebarIcon, path: '/freight-forwarding/billing', roles: ['dispatcher', 'admin', 'economist'], featureCodes: [FEATURE_CODES.TRANSPORT_MANAGEMENT, FEATURE_CODES.FREIGHT_FORWARDING] },
      { id: 'billing', label: t('billing'), icon: BillingSidebarIcon, path: '/billing', roles: ['dispatcher', 'admin', 'economist'], featureCode: FEATURE_CODES.EFACTURA },
      { id: 'profitability', label: t('profitability'), icon: ProfitabilitySidebarIcon, path: '/profitability/transports', roles: ['dispatcher', 'admin', 'economist'], featureCode: FEATURE_CODES.PROFITABILITY },
    ];

    return allItems.filter((item) => {
      const roleAllowed = item.roles.includes('all') || item.roles.includes(userRole);
      if (!roleAllowed) return false;

      if (userRole === 'economist' && !['dashboard', 'billing', 'freight-billing'].includes(item.id) && !hasFeature(userData?.features, FEATURE_CODES.PROFITABILITY)) {
        return false;
      }

      if (item.featureCodes) {
        return item.featureCodes.every((code) => hasFeature(userData?.features, code));
      }
      if (item.featureCodesAny) {
        return item.featureCodesAny.some((code) => hasFeature(userData?.features, code));
      }
      if (item.featureCode) {
        return hasFeature(userData?.features, item.featureCode);
      }

      return true;
    });
  };

  const menuItems = useMemo(() => getMenuItems(), [userData, t]);

  const isActive = useCallback((path) => {
    if (path === '/profitability/transports') {
      return location.pathname.startsWith('/profitability');
    }
    if (path === '/billing') {
      return location.pathname.startsWith('/billing');
    }
    if (path === '/freight-forwarding/billing') {
      return location.pathname.startsWith('/freight-forwarding/billing');
    }
    if (path === '/fleet/cars' || path === '/fleet/vans') {
      return location.pathname.startsWith(path);
    }
    if (path === '/fleet/maintenance') {
      return location.pathname.startsWith('/fleet/maintenance');
    }
    return location.pathname === path;
  }, [location.pathname]);

  const handleNavigation = useCallback((path) => {
    navigate(path);
    if (!isDesktop && !isTablet) {
      setIsMobileOpen(false);
    } else if (isExpanded) {
      setIsExpanded(false);
    }
  }, [navigate, isDesktop, isTablet, isExpanded]);

  // Close mobile menu on route change
  useEffect(() => {
    if (!isDesktop) {
      setIsMobileOpen(false);
    }
  }, [location.pathname, isDesktop]);

  // Handle window resize and Chrome scroll fix
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const desktop = width >= 1024;
      const tablet = width >= 768 && width < 1024;

      setIsDesktop(desktop);
      setIsTablet(tablet);

      if (desktop) {
        setIsMobileOpen(false);
      }
    };

    // Chrome scroll fix - ensure sidebar stays fixed
    const handleScroll = () => {
      if (isChrome) {
        const sidebar = document.querySelector('.modern-sidebar');
        if (sidebar) {
          // Force position fixed on scroll
          sidebar.style.position = 'fixed';
          sidebar.style.top = '0px';
          sidebar.style.left = '0px';
        }
      }
    };

    handleResize(); // Run once on mount
    window.addEventListener('resize', handleResize);

    // Add scroll listener for Chrome
    if (isChrome) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (isChrome) {
        window.removeEventListener('scroll', handleScroll);
        document.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isChrome]);

  // Update main content margin
  useEffect(() => {
    if (isDesktop || isTablet) {
      if (isExpanded) {
        document.documentElement.classList.add('sidebar-expanded');
        document.documentElement.classList.remove('sidebar-collapsed');
      } else {
        document.documentElement.classList.add('sidebar-collapsed');
        document.documentElement.classList.remove('sidebar-expanded');
      }
    } else {
      document.documentElement.classList.remove('sidebar-collapsed', 'sidebar-expanded');
    }

    // Add browser-specific classes - run only once on mount
    if (isChrome) {
      document.documentElement.classList.add('is-chrome');
      document.body.classList.add('chrome-browser');

      // Force fix position on Chrome
      const sidebar = document.querySelector('.modern-sidebar');
      if (sidebar) {
        sidebar.style.position = 'fixed';
        sidebar.style.top = '0';
        sidebar.style.left = '0';
        sidebar.style.bottom = '0';
        sidebar.style.height = '100vh';
        sidebar.style.zIndex = '9999';

        // Force repaint to ensure Chrome applies the styles
        sidebar.offsetHeight; // Trigger reflow
      }
    }
    if (isSafari) {
      document.documentElement.classList.add('is-safari');
      document.body.classList.add('safari-browser');
    }
  }, [isExpanded, isDesktop, isTablet, isChrome, isSafari]);

  return (
    <>
      {/* Mobile Menu Toggle */}
      {!isDesktop && !isTablet && (
        <button
          onClick={() => setIsMobileOpen(prev => !prev)}
          className="fixed top-4 left-4 z-50 p-2.5 rounded-lg bg-white shadow-lg hover:shadow-xl transition-shadow duration-100"
          aria-label={t('toggleMenu')}
        >
          {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      )}

      {/* Mobile Overlay */}
      {!isDesktop && !isTablet && isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 transition-opacity duration-150"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Container */}
      <aside
        className={`
          modern-sidebar ${isDesktop ? 'desktop-instant' : ''} ${isChrome ? 'chrome-browser' : ''} ${isSafari ? 'safari-browser' : ''}
          fixed top-0 left-0 h-screen z-40
          bg-white
          ${isDesktop
            ? isChrome
              ? 'transition-[width] duration-[100ms] ease-out'  // Chrome needs longer duration
              : 'transition-[width] duration-[20ms] ease-linear' // Safari can handle ultra-fast
            : 'transition-all duration-150 ease-out'
          }
          ${isDesktop
            ? (isExpanded ? 'w-64' : 'w-20')
            : isTablet
            ? (isExpanded ? 'w-64' : 'w-20')
            : (isMobileOpen ? 'w-80 translate-x-0' : 'w-80 -translate-x-full')
          }
        `}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: 40,
          willChange: isDesktop && !isChrome ? 'width' : 'auto', // Chrome doesn't like will-change
          transform: isDesktop && isSafari ? 'translateZ(0)' : undefined,
          backfaceVisibility: isSafari ? 'hidden' : undefined
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          {(isDesktop || isTablet) && (
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              {isExpanded && (
                <img
                  src={AtruxLogo}
                  alt="AtruX"
                  className={`h-7 w-auto object-contain transition-opacity ${isChrome ? 'duration-[100ms]' : 'duration-[20ms]'}`}
                />
              )}
              <button
                onClick={() => setIsExpanded(prev => !prev)}
                className={`p-2 rounded-lg hover:bg-[rgba(0,90,224,0.12)] transition-colors ${isChrome ? 'duration-[100ms]' : 'duration-[20ms]'} ml-auto`}
                aria-label={isExpanded ? t('collapseSidebar') : t('expandSidebar')}
              >
                {isExpanded ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto p-4">
            <ul className="space-y-2">
              {menuItems.map((item, index) => {
                const active = isActive(item.path);
                const Icon = item.icon;

                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className={`
                        modern-nav-item group
                        w-full flex items-center gap-3
                        px-3 py-3 rounded-lg
                        ${isDesktop
                          ? isChrome
                            ? 'transition-all duration-[100ms] ease-out'
                            : 'transition-all duration-[20ms] ease-out'
                          : 'transition-all duration-100 ease-out'
                        }
                        relative overflow-hidden
                        ${active
                          ? 'bg-[rgba(0,90,224,0.6)] text-white shadow-lg'
                          : 'hover:bg-[rgba(0,90,224,0.12)] text-gray-700 hover:text-[#005AE0]'
                        }
                        ${(isDesktop || isTablet) && !isExpanded ? 'justify-center' : ''}
                      `}
                      style={{
                        animationDelay: isDesktop ? '0ms' : `${index * 10}ms`
                      }}
                    >
                      {/* Active indicator */}
                      {active && (
                        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(0,90,224,0.6)] to-[rgba(0,90,224,0.45)] opacity-0 animate-pulse" />
                      )}

                      {/* Icon */}
                      <div className={`
                        relative z-10 flex items-center justify-center
                        text-black
                        ${isDesktop ? '' : 'transition-transform duration-75'}
                        ${active ? 'scale-110' : 'hover:scale-110'}
                      `}>
                        <Icon size={20} className="sidebar-icon-monochrome" />
                      </div>

                      {/* Label */}
                      {((!isDesktop && !isTablet) || isExpanded) && (
                        <span className={`
                          relative z-10
                          ${isDesktop
                            ? isChrome
                              ? 'transition-all duration-[100ms]'
                              : 'transition-all duration-[20ms]'
                            : 'transition-all duration-100'
                          }
                          ${(isDesktop || isTablet) ? (isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4') : ''}
                          ${active ? 'font-medium' : 'font-normal'}
                          whitespace-nowrap
                        `}>
                          {item.label}
                        </span>
                      )}

                      {/* Badge */}
                      {item.badge && ((!isDesktop && !isTablet) || isExpanded) && (
                        <span className={`
                          ml-auto px-2 py-0.5 text-xs font-medium
                          rounded-full ${isDesktop
                            ? isChrome
                              ? 'transition-all duration-[100ms]'
                              : 'transition-all duration-[20ms]'
                            : 'transition-all duration-100'
                          }
                          ${active
                            ? 'bg-white/20 text-white'
                            : 'bg-[rgba(0,90,224,0.12)] text-[#005AE0]'
                          }
                          ${(isDesktop || isTablet) ? (isExpanded ? 'opacity-100 scale-100' : 'opacity-0 scale-75') : ''}
                        `}>
                          {item.badge}
                        </span>
                      )}

                      {/* Tooltip for collapsed state */}
                      {(isDesktop || isTablet) && !isExpanded && (
                        <div className="
                          absolute left-full ml-2 px-3 py-2
                          bg-gray-800 text-white text-sm
                          rounded-lg whitespace-nowrap
                          opacity-0 pointer-events-none
                          ${isDesktop ? '' : 'transition-opacity duration-75'}
                          group-hover:opacity-100
                          z-50 shadow-xl
                        ">
                          {item.label}
                          {item.badge && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs bg-white/20 rounded">
                              {item.badge}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Footer - Profile */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={() => handleNavigation('/notifications')}
              className={`
                w-full flex items-center gap-3
                px-3 py-3 rounded-lg
                ${isDesktop
                  ? isChrome
                    ? 'transition-all duration-[100ms]'
                    : 'transition-all duration-[20ms]'
                  : 'transition-all duration-100'
                }
                ${isActive('/notifications')
                  ? 'bg-[rgba(0,90,224,0.6)] text-white'
                  : 'hover:bg-[rgba(0,90,224,0.12)] text-gray-700 hover:text-[#005AE0]'
                }
                ${(isDesktop || isTablet) && !isExpanded ? 'justify-center' : ''}
              `}
            >
              <div className="relative">
                <IconNotificari width={20} height={20} className="sidebar-icon-monochrome" />
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              {((!isDesktop && !isTablet) || isExpanded) && (
                <span className={`
                  ${isDesktop
                  ? isChrome
                    ? 'transition-all duration-[100ms]'
                    : 'transition-all duration-[20ms]'
                  : 'transition-all duration-100'
                }
                  ${(isDesktop || isTablet) ? (isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4') : ''}
                `}>
                  {t('notifications')}
                </span>
              )}
            </button>
            <button
              onClick={() => handleNavigation('/profile')}
              className={`
                w-full flex items-center gap-3
                px-3 py-3 rounded-lg
                ${isDesktop
                  ? isChrome
                    ? 'transition-all duration-[100ms]'
                    : 'transition-all duration-[20ms]'
                  : 'transition-all duration-100'
                }
                ${isActive('/profile')
                  ? 'bg-[rgba(0,90,224,0.6)] text-white'
                  : 'hover:bg-[rgba(0,90,224,0.12)] text-gray-700 hover:text-[#005AE0]'
                }
                ${(isDesktop || isTablet) && !isExpanded ? 'justify-center' : ''}
              `}
            >
              <ProfilSidebarIcon size={20} className="sidebar-icon-monochrome" />
              {((!isDesktop && !isTablet) || isExpanded) && (
                <span className={`
                  ${isDesktop
                  ? isChrome
                    ? 'transition-all duration-[100ms]'
                    : 'transition-all duration-[20ms]'
                  : 'transition-all duration-100'
                }
                  ${(isDesktop || isTablet) ? (isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4') : ''}
                `}>
                  {t('profile')}
                </span>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default ModernSidebar;
